import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FaFileInvoiceDollar, FaBolt, FaCheckCircle } from 'react-icons/fa';
import SecureServiceBase from './SecureServiceBase';

const SecureBillPaymentModal = ({ show, onHide, onSuccess }) => {
  const [billType, setBillType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Bill payment providers
  const billProviders = [
    { id: 'electricity', name: 'Electricity', icon: '⚡', category: 'Utilities' },
    { id: 'water', name: 'Water', icon: '💧', category: 'Utilities' },
    { id: 'gas', name: 'Gas', icon: '🔥', category: 'Utilities' },
    { id: 'internet', name: 'Internet', icon: '🌐', category: 'Telecom' },
    { id: 'mobile_postpaid', name: 'Mobile Postpaid', icon: '📱', category: 'Telecom' },
    { id: 'dth', name: 'DTH', icon: '📺', category: 'Entertainment' },
    { id: 'insurance', name: 'Insurance', icon: '🛡️', category: 'Insurance' },
    { id: 'loan', name: 'Loan EMI', icon: '🏦', category: 'Finance' },
    { id: 'credit_card', name: 'Credit Card', icon: '💳', category: 'Finance' }
  ];

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000];

  useEffect(() => {
    if (show) {
      setProviders(billProviders);
    }
  }, [show]);

  const handleAmountChange = (val, validateAmount) => {
    setAmount(val);
    if (validateAmount) {
      validateAmount(val);
    }
  };

  const handleSubmit = (e, submitHandler) => {
    e.preventDefault();
    
    if (!billType || !customerId || !amount || !selectedProvider) {
      return;
    }

    const formData = {
      bill_type: billType,
      customer_id: customerId,
      amount: Number(amount),
      provider: selectedProvider,
      type: 'bill_payment'
    };

    submitHandler(formData);
  };

  return (
    <SecureServiceBase
      show={show}
      onHide={onHide}
      onSuccess={onSuccess}
      serviceName="Bill Payment"
      serviceEndpoint="/api/bill-payment/process"
      requiresBalance={true}
      minAmount={10}
    >
      {({ balance, error, loading, validateAmount, handleSubmit, isFormValid }) => (
        <form onSubmit={(e) => handleSubmit(e, handleSubmit)}>
          {/* Bill Type Selection */}
          <div className="mb-3">
            <label className="form-label fw-bold">Select Bill Type</label>
            <div className="row g-2">
              {providers.map((provider) => (
                <div key={provider.id} className="col-md-4 col-sm-6">
                  <Card 
                    className={`h-100 cursor-pointer ${billType === provider.id ? 'border-primary' : ''}`}
                    onClick={() => setBillType(provider.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="text-center p-3">
                      <div className="fs-2 mb-2">{provider.icon}</div>
                      <h6 className="mb-1">{provider.name}</h6>
                      <Badge bg="secondary" className="small">{provider.category}</Badge>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Customer ID Input */}
          <div className="mb-3">
            <label htmlFor="customerId" className="form-label">
              Customer ID / Account Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="customerId"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Enter customer ID or account number"
              disabled={loading}
              required
            />
          </div>

          {/* Amount Input */}
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Amount <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="number"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="amount"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value, validateAmount)}
                placeholder="Enter amount"
                min="10"
                step="1"
                disabled={loading}
                required
              />
            </div>
            {error && (
              <div className="invalid-feedback d-block">
                {error}
              </div>
            )}
            {balance !== null && amount && !error && (
              <div className="form-text text-success">
                Remaining balance: ₹{(balance - Number(amount)).toFixed(2)}
              </div>
            )}
          </div>

          {/* Quick Amount Selection */}
          <div className="mb-3">
            <label className="form-label">Quick Amounts</label>
            <div className="d-flex flex-wrap gap-2">
              {quickAmounts.map((quickAmount) => (
                <Button
                  key={quickAmount}
                  variant={amount === quickAmount.toString() ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => handleAmountChange(quickAmount.toString(), validateAmount)}
                  disabled={loading}
                >
                  ₹{quickAmount}
                </Button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-grid">
            <Button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid || !billType || !customerId || !amount}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </span>
                  Processing...
                </>
              ) : (
                <>
                  <FaBolt className="me-2"></FaBolt>
                  Pay ₹{amount || '0'}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </SecureServiceBase>
  );
};

export default SecureBillPaymentModal;
